<template>
  <div>
    <b-card>
      <TitleTable titleTable="IV Add-on prices" iconTable="fas fa-dollar-sign" />
      <p class="text-muted px-2 mb-2 small">
        Prices are append-only. To change a price, add a new record with a later effective date.
        Leave facility empty for a global price.
      </p>
      <div class="m-1">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <treeselect
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="w-25" />
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <GoBack class="mr-2" />
              <b-button v-if="$can('store', 'iv-price')" variant="primary" @click="openAdd">Add price</b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No prices recorded">
        <template #cell(iv_addon_id)="data">
          {{ addonLabel(data.item) }}
        </template>
        <template #cell(facility)="data">
          {{ facilityLabel(data.item) }}
        </template>
        <template #cell(price)="data">
          {{ displayPrice(data.value) }}
        </template>
        <template #cell(effective_date)="data">
          {{ formatProjectDateDisplay(data.value) }}
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="$can('update', 'iv-price')"
            size="sm"
            variant="outline-primary"
            class="mr-1"
            v-b-tooltip.hover
            title="Edit"
            @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
          <b-button
            v-if="$can('store', 'iv-price')"
            size="sm"
            variant="outline-primary"
            v-b-tooltip.hover
            title="Create from this"
            @click="openCreateFrom(data.item)">
            <i class="fas fa-plus mr-1"></i>
            Create from this
          </b-button>
        </template>
      </b-table>
      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="currentPage"
        @page-changed="pageChanged" />
    </b-card>

    <IvFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="iv-price-sidebar"
      :title="sidebarTitle"
      :submit-label="submitLabel"
      :loading="saving"
      @submit="save"
      @close="onSidebarClose">
      <b-form-group label="Add-on *">
        <treeselect
          v-model="form.iv_addon_id"
          :options="addonOptions"
          :clearable="false"
          :disabled="isEdit"
          placeholder="Select add-on" />
      </b-form-group>
      <b-form-group label="Facility">
        <treeselect
          v-if="isEdit"
          v-model="form.facility_id"
          :options="facilityOptions"
          :normalizer="normalizer"
          :clearable="true"
          placeholder="All facilities"
          :disabled="true" />
        <treeselect
          v-else
          v-model="form.facility_ids"
          :options="facilityOptions"
          :normalizer="normalizer"
          :multiple="true"
          :clearable="true"
          placeholder="Select facilities"
          :disabled="facilityOptionsLoading || saving" />
        <small class="text-muted d-block mt-1">
          {{ facilityHelp }}
        </small>
      </b-form-group>
      <b-form-group label="Price *">
        <b-form-input v-model="form.price" type="number" step="0.01" min="0" />
      </b-form-group>
      <b-form-group label="Effective from *">
        <flat-pickr
          v-model="form.effective_date"
          :config="dateConfig"
          class="form-control"
          placeholder="MM-DD-YYYY"
          @on-change="onEffectiveDateChange" />
        <small class="text-muted d-block mt-1">Format: MM-DD-YYYY</small>
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-input v-model="form.notes" placeholder="Optional" />
      </b-form-group>
    </IvFormSidebar>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import ToastNotification from '@/components/ToastNotification'
import PaginationTable from '@/components/PaginationTable'
import IvFormSidebar from './IvFormSidebar.vue'
import ivApi from '@/core/services/api/admin/iv'
import axiosF from '@/core/services/api/admin/facilities'
import {
  todayProjectDate,
  toProjectDate,
  formatProjectDateDisplay,
} from '@/core/services/api/weightloss-inventory/dateFormat'

function emptyForm() {
  return {
    iv_addon_id: null,
    facility_id: null,
    facility_ids: [],
    price: '',
    effective_date: todayProjectDate(),
    notes: '',
  }
}

function toFacilityId(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export default {
  name: 'TableIvPrices',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BFormGroup,
    BFormInput,
    flatPickr,
    TitleTable,
    GoBack,
    ToastNotification,
    PaginationTable,
    IvFormSidebar,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      items: [],
      addonOptions: [],
      addonsById: {},
      facilityOptions: [],
      facilityOptionsLoading: false,
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'iv_addon_id', label: 'Add-on' },
        { key: 'facility', label: 'Facility' },
        { key: 'price', label: 'Price' },
        { key: 'effective_date', label: 'Effective from' },
        { key: 'notes', label: 'Notes' },
        { key: 'actions', label: '' },
      ],
      sidebarVisible: false,
      saving: false,
      mode: 'create',
      editId: null,
      form: emptyForm(),
      perPage: 10,
      totalUsers: 0,
      currentPage: { page: 1 },
      dataMeta: { from: 1, to: 1, of: 1 },
      perPageOptions: [10, 25, 50, 100].map(i => ({
        id: i,
        label: `Show ${i} Entries`,
      })),
      dateConfig: {
        dateFormat: 'm-d-Y',
        allowInput: true,
      },
    }
  },
  computed: {
    isEdit() {
      return this.mode === 'edit'
    },
    sidebarTitle() {
      if (this.mode === 'edit') return 'Edit IV price'
      if (this.mode === 'createFrom') return 'Create from this'
      return 'New IV price'
    },
    submitLabel() {
      return this.mode === 'edit' ? 'Save' : 'Save'
    },
    facilityHelp() {
      if (this.isEdit) return 'Facility cannot be changed when editing.'
      return 'Select one or more facilities. Leave empty to apply to all facilities.'
    },
  },
  mounted() {
    this.init()
  },
  watch: {
    perPage() {
      this.load(1)
    },
  },
  methods: {
    formatProjectDateDisplay,
    async init() {
      await Promise.all([this.loadFacilities(), this.loadAddons()])
      this.load()
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        this.facilityOptions = []
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    async loadAddons() {
      try {
        const result = await ivApi.listAddons(200, 1)
        const addons = result.items || []
        this.addonsById = {}
        this.addonOptions = addons.map(a => {
          this.addonsById[a.id] = a
          return { id: a.id, label: a.name }
        })
      } catch (e) {
        this.addonOptions = []
      }
    },
    displayPrice(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? `$${n.toFixed(2)}` : value
    },
    addonLabel(item) {
      if (item?.iv_addon?.name) return item.iv_addon.name
      const id = item?.iv_addon_id
      return this.addonsById[id]?.name || (id != null ? `#${id}` : '—')
    },
    facilityLabel(item) {
      if (item?.facility?.name) return item.facility.name
      const id = item?.facility_id
      if (id == null || id === '') return 'All facilities'
      const f = this.facilityOptions.find(x => x.id === id || x.id === Number(id))
      return f ? f.name : `#${id}`
    },
    facilityIdsFromItem(item) {
      const id = toFacilityId(item?.facility_id)
      return id == null ? [] : [id]
    },
    formFromItem(item, { keepDate = false } = {}) {
      return {
        iv_addon_id: item.iv_addon_id,
        facility_id: toFacilityId(item.facility_id),
        facility_ids: this.facilityIdsFromItem(item),
        price: item.price != null ? item.price : '',
        effective_date: keepDate
          ? toProjectDate(item.effective_date) || todayProjectDate()
          : todayProjectDate(),
        notes: item.notes || '',
      }
    },
    onSidebarClose() {
      this.mode = 'create'
      this.editId = null
      this.form = emptyForm()
    },
    openAdd() {
      this.mode = 'create'
      this.editId = null
      this.form = emptyForm()
      this.sidebarVisible = true
    },
    openEdit(item) {
      this.mode = 'edit'
      this.editId = item.id
      this.form = this.formFromItem(item, { keepDate: true })
      this.sidebarVisible = true
    },
    openCreateFrom(item) {
      this.mode = 'createFrom'
      this.editId = null
      this.form = this.formFromItem(item)
      this.sidebarVisible = true
    },
    onEffectiveDateChange(selectedDates, dateStr) {
      const raw = dateStr || (selectedDates && selectedDates[0]) || this.form.effective_date
      this.form.effective_date = toProjectDate(raw) || ''
    },
    async load(page = 1) {
      try {
        const result = await ivApi.listPrices(this.perPage || 10, page)
        this.items = result.items || []
        this.totalUsers = result.total || 0
        this.dataMeta.of = result.total || 0
        this.dataMeta.from = result.from || 0
        this.dataMeta.to = result.to || 0
        this.currentPage.page = result.current_page || page
      } catch (e) {
        this.$refs.toast.danger(e.message)
      }
    },
    pageChanged(page) {
      this.load(page)
    },
    buildPayload(facilityId) {
      const payload = {
        iv_addon_id: Number(this.form.iv_addon_id),
        facility_id: facilityId,
        price: Number(this.form.price),
        effective_date: toProjectDate(this.form.effective_date),
      }
      const notes = (this.form.notes || '').trim()
      if (notes) payload.notes = notes
      return payload
    },
    validateForm() {
      if (!this.form.iv_addon_id) {
        this.$refs.toast.danger('Add-on is required')
        return false
      }
      if (this.form.price === '' || this.form.price == null) {
        this.$refs.toast.danger('Price is required')
        return false
      }
      const effective = toProjectDate(this.form.effective_date)
      if (!effective || !/^\d{2}-\d{2}-\d{4}$/.test(effective)) {
        this.$refs.toast.danger('Effective date is required (MM-DD-YYYY)')
        return false
      }
      this.form.effective_date = effective
      return true
    },
    createTargets() {
      const ids = (this.form.facility_ids || [])
        .map(toFacilityId)
        .filter(id => id != null)
      return ids.length ? ids : [null]
    },
    async save() {
      if (!this.validateForm()) return
      this.saving = true
      try {
        if (this.isEdit) {
          await this.saveEdit()
        } else {
          await this.saveCreate()
        }
        this.sidebarVisible = false
        this.load(this.currentPage.page)
      } catch (e) {
        this.$refs.toast.danger(e.message || 'Could not save price')
      } finally {
        this.saving = false
      }
    },
    async saveEdit() {
      const payload = this.buildPayload(toFacilityId(this.form.facility_id))
      try {
        await ivApi.updatePrice(this.editId, payload)
        this.$refs.toast.success('Price updated')
      } catch (e) {
        if (e.status === 404 || e.status === 405) {
          await ivApi.createPrice(payload)
          this.$refs.toast.success('Price version recorded')
          return
        }
        throw e
      }
    },
    async saveCreate() {
      const targets = this.createTargets()
      const results = await Promise.allSettled(
        targets.map(facilityId => ivApi.createPrice(this.buildPayload(facilityId)))
      )
      const failed = results.filter(r => r.status === 'rejected')
      if (failed.length === results.length) {
        throw failed[0].reason || new Error('Could not save price')
      }
      if (failed.length) {
        this.$refs.toast.danger(
          `${failed.length} of ${targets.length} prices could not be saved`
        )
        return
      }
      const count = targets.length
      this.$refs.toast.success(
        count > 1 ? `Price recorded for ${count} facilities` : 'Price recorded'
      )
    },
  },
}
</script>
